import { useCallback, useEffect, useRef, useState } from 'react';
import {
  TrackeamentoApi,
  TrackeamentoCheckoutDto,
} from '../api/trackeamento.api';
import { getUrlParams } from '../utils/get-url-params';

const DEBOUNCE_MS = 600;

/**
 * Persiste/atualiza o trackeamento do comprador no backend conforme ele
 * preenche os campos do checkout. Cada chamada reseta o relógio do
 * webhook de carrinho abandonado no microserviço.
 *
 * Não depende de pixel — basta o checkoutId.
 *
 * Estratégia:
 *  - Mantém o `trackeamentoId` retornado pelo POST inicial em estado
 *    local, para que chamadas seguintes virem PUT na mesma linha.
 *  - Faz debounce de ~600ms para não estourar request a cada keystroke.
 *  - Só envia se houver pelo menos email ou telefone preenchido (caso
 *    contrário o MS não agenda nada e a request seria desperdiçada).
 */
export function useCarrinhoAbandonadoTracking(checkoutId: string | undefined) {
  const apiRef = useRef(new TrackeamentoApi());
  const [trackeamentoId, setTrackeamentoId] = useState<string | undefined>();
  const trackeamentoIdRef = useRef<string | undefined>();
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastPayloadRef = useRef<string>('');

  useEffect(() => {
    trackeamentoIdRef.current = trackeamentoId;
  }, [trackeamentoId]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const track = useCallback(
    (data: Omit<TrackeamentoCheckoutDto, 'checkoutId' | 'id'>) => {
      if (!checkoutId) return;
      if (getUrlParams('recuperaai_cart_id')) return;

      const temContato =
        Boolean(data.email?.trim()) || Boolean(data.telefone?.trim());
      if (!temContato) return;

      const payload: TrackeamentoCheckoutDto = {
        ...data,
        checkoutId,
        ...(trackeamentoIdRef.current
          ? { id: trackeamentoIdRef.current }
          : {}),
      };

      const serialized = JSON.stringify(payload);
      if (serialized === lastPayloadRef.current) return;
      lastPayloadRef.current = serialized;

      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(async () => {
        try {
          const response = trackeamentoIdRef.current
            ? await apiRef.current.atualizar(payload)
            : await apiRef.current.criar(payload);

          const id = response?.data?.id;
          if (id && !trackeamentoIdRef.current) {
            trackeamentoIdRef.current = id;
            setTrackeamentoId(id);
          }
        } catch {
          // silencioso: tracking é best-effort e não bloqueia o checkout
        }
      }, DEBOUNCE_MS);
    },
    [checkoutId],
  );

  return { track, trackeamentoId };
}
